import { useMemo } from 'react';

import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  SimpleGrid,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import GroupedAmountInput from 'components/GroupedAmountInput';
import { Currency } from 'components/Number';
import PropTypes from 'prop-types';

import { formatMonthLabel } from './cashflowFeeExposureHelpers';
import {
  companyCashRows,
  companyCashStatement,
  customerCashSeries,
  monthRange,
  monthlyRateFromAnnualIrr,
  withLenders,
} from './companyCash';

const SECTION_BG = '#1F4E79';
const SECTION_COLOR = '#FFFFFF';
const TOTAL_BG = '#D6E4F0';

function Field({ label, children }) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" mb={1}>{label}</Text>
      {children}
    </Box>
  );
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

function AmountField({ label, value, onChange, readOnly }) {
  return (
    <Field label={label}>
      {readOnly ? (
        <Text fontSize="sm" fontWeight={600}><Currency value={Number(value) || 0} maximumFractionDigits={0} /></Text>
      ) : (
        <GroupedAmountInput width="100%" aria-label={label} value={value ?? ''} onChange={onChange} />
      )}
    </Field>
  );
}

AmountField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

function RateField({ label, value, onChange, readOnly }) {
  return (
    <Field label={label}>
      {readOnly ? (
        <Text fontSize="sm" fontWeight={600}>{`${Number(value) || 0}%`}</Text>
      ) : (
        <Input
          size="sm"
          px={2}
          inputMode="decimal"
          aria-label={label}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value.replace(/[^\d.]/g, ''))}
        />
      )}
    </Field>
  );
}

RateField.propTypes = AmountField.propTypes;

export function monthlyRateText(annualPct) {
  const monthly = monthlyRateFromAnnualIrr(annualPct);
  return `Monthly rate ${monthly.toFixed(3)}% (${Number(annualPct) || 0}% a year, compounded monthly)`;
}

// IrrField is an annual IRR input; hovering it shows the monthly rate that is charged.
function IrrField(props) {
  const { value } = props;
  return (
    <Tooltip hasArrow placement="top" label={monthlyRateText(value)}>
      <Box>
        <RateField {...props} />
      </Box>
    </Tooltip>
  );
}

IrrField.propTypes = AmountField.propTypes;

function MonthField({ label, value, onChange, readOnly }) {
  return (
    <Field label={label}>
      {readOnly ? (
        <Text fontSize="sm" fontWeight={600}>{value ? formatMonthLabel(value) : '—'}</Text>
      ) : (
        <Input size="sm" px={2} type="month" aria-label={label} value={value || ''} onChange={(event) => onChange(event.target.value)} />
      )}
    </Field>
  );
}

MonthField.propTypes = AmountField.propTypes;

export function FinancingInputs({ inputs, onChange, readOnly }) {
  const set = (key) => (value) => onChange({ ...inputs, [key]: value });
  const lenders = Array.isArray(inputs.lenders) ? inputs.lenders : [];
  const setLender = (index, patch) => set('lenders')(
    lenders.map((lender, position) => (position === index ? { ...lender, ...patch } : lender))
  );

  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="12px" p={4} mb={4}>
      <SimpleGrid columns={{ base: 1, md: 3, xl: 7 }} spacing={3} mb={4}>
        <AmountField label="Opening cash" value={inputs.openingCash} onChange={set('openingCash')} readOnly={readOnly} />
        <AmountField label="Minimum close balance" value={inputs.minCloseBalance} onChange={set('minCloseBalance')} readOnly={readOnly} />
        <AmountField label="Operating expenses per month" value={inputs.monthlyOpex} onChange={set('monthlyOpex')} readOnly={readOnly} />
        <AmountField label="Capital fund" value={inputs.capitalAmount} onChange={set('capitalAmount')} readOnly={readOnly} />
        <MonthField label="Capital arrives" value={inputs.capitalMonth} onChange={set('capitalMonth')} readOnly={readOnly} />
        <RateField label="AR facility cap % of next month" value={inputs.arCapPct} onChange={set('arCapPct')} readOnly={readOnly} />
        <IrrField label="AR facility fee - annual IRR %" value={inputs.arRate} onChange={set('arRate')} readOnly={readOnly} />
      </SimpleGrid>
      <Flex align="center" gap={3} wrap="wrap" mb={lenders.length ? 2 : 0}>
        <Text fontSize="sm" fontWeight={600}>Lenders</Text>
        {!readOnly && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => set('lenders')([...lenders, { name: '', cap: '', rate: '', startMonth: '' }])}
          >
            Add lender
          </Button>
        )}
        {!lenders.length && <Text fontSize="sm" color="gray.500">No lenders yet.</Text>}
      </Flex>
      {lenders.map((lender, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Flex key={index} align="flex-end" gap={3} mb={2} wrap="wrap">
          <Text fontSize="sm" fontWeight={700} w="80px" pb={2}>{`Lender ${index + 1}`}</Text>
          <Box w="200px">
            <Field label="Name">
              {readOnly ? (
                <Text fontSize="sm" fontWeight={600}>{lender.name || '—'}</Text>
              ) : (
                <Input
                  size="sm"
                  px={2}
                  aria-label={`Lender ${index + 1} name`}
                  placeholder="e.g. Seed notes"
                  value={lender.name || ''}
                  onChange={(event) => setLender(index, { name: event.target.value })}
                />
              )}
            </Field>
          </Box>
          <Box w="160px">
            <AmountField label="Cap" value={lender.cap} onChange={(value) => setLender(index, { cap: value })} readOnly={readOnly} />
          </Box>
          <Box w="130px">
            <IrrField label="Annual IRR %" value={lender.rate} onChange={(value) => setLender(index, { rate: value })} readOnly={readOnly} />
          </Box>
          <Box w="160px">
            <MonthField label="Start month" value={lender.startMonth} onChange={(value) => setLender(index, { startMonth: value })} readOnly={readOnly} />
          </Box>
          {!readOnly && (
            <IconButton
              size="sm"
              variant="ghost"
              aria-label={`Remove lender ${index + 1}`}
              icon={<CloseIcon boxSize={2.5} />}
              onClick={() => set('lenders')(lenders.filter((_, position) => position !== index))}
            />
          )}
        </Flex>
      ))}
      <Text fontSize="xs" color="gray.500" mt={3}>
        Each month, a gap below the minimum close is drawn from the cheapest source with room: the AR facility or a
        lender whose start month has come (an empty start month means available from the first month). With equal
        rates, AR goes first, then the lower lender number. The AR facility lends up to its cap % of next month&apos;s
        customer cash (returns plus fees): with 1,000,000 coming in February and an 80% cap, January can borrow up to
        800,000. It is repaid in full the next month with one month of fee. Rates are annual IRR. Each month charges
        the monthly rate that compounds to it, (1 + IRR)^(1/12) - 1; hover a rate to see it. 24% a year is 1.809% a
        month, so 89,000 drawn on the AR facility in November costs 1,610, paid in December. Cash above the minimum
        close repays the most expensive lender first. Returns already include the fee a customer pays; Fee (customers) is the facility fee paid separately.
      </Text>
    </Box>
  );
}

FinancingInputs.propTypes = {
  inputs: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

function CompanyCashStatement({ customers, startMonth, endMonth, inputs, onInputsChange, readOnly, inputsOnly = false }) {
  const shownInputs = useMemo(() => withLenders(inputs), [inputs]);
  const rows = useMemo(() => {
    const months = monthRange(startMonth, endMonth);
    return companyCashStatement(customerCashSeries(customers, months), shownInputs);
  }, [customers, startMonth, endMonth, shownInputs]);
  const lines = useMemo(() => companyCashRows(shownInputs.lenders), [shownInputs]);

  return (
    <Box>
      <FinancingInputs inputs={shownInputs} onChange={onInputsChange} readOnly={readOnly || !onInputsChange} />
      {inputsOnly ? null : !rows.length ? (
        <Text color="gray.500">Choose From and Through months on Customers to see the statement.</Text>
      ) : (
        <Box overflowX="auto">
          <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
            <thead>
              <tr>
                <th style={stickyHeader}>Line Item</th>
                {rows.map((row) => (
                  <th key={row.month} style={monthHeader}>{formatMonthLabel(row.month)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const section = line.kind === 'section';
                const total = line.kind === 'total';
                const background = section ? SECTION_BG : total ? TOTAL_BG : undefined;
                return (
                  <tr key={line.label}>
                    <td
                      style={{
                        ...stickyCell,
                        background: background || '#fff',
                        color: section ? SECTION_COLOR : undefined,
                        fontWeight: section || total ? 700 : 500,
                      }}
                    >
                      {line.label}
                    </td>
                    {rows.map((row) => {
                      const value = line.value ? line.value(row) : null;
                      const warn = line.kind === 'warning' && value > 0;
                      const negativeClose = line.key === 'closeBalance' && value < 0;
                      return (
                        <td
                          key={`${line.label}-${row.month}`}
                          style={{
                            padding: '8px 12px',
                            textAlign: 'right',
                            background,
                            color: section ? SECTION_COLOR : (warn || negativeClose) ? '#D1352A' : undefined,
                            fontWeight: total ? 700 : undefined,
                          }}
                        >
                          {line.value && value ? <Currency value={value} maximumFractionDigits={0} /> : ''}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>
      )}
    </Box>
  );
}

const stickyHeader = {
  position: 'sticky',
  left: 0,
  background: '#fff',
  textAlign: 'left',
  padding: '8px 12px',
  zIndex: 1,
};

const monthHeader = {
  padding: '8px 12px',
  textAlign: 'right',
  color: '#fff',
  fontWeight: 700,
  background: '#2E75B6',
};

const stickyCell = {
  position: 'sticky',
  left: 0,
  padding: '8px 12px',
  zIndex: 1,
  minWidth: 280,
};

CompanyCashStatement.propTypes = {
  customers: PropTypes.array,
  startMonth: PropTypes.string,
  endMonth: PropTypes.string,
  inputs: PropTypes.object,
  onInputsChange: PropTypes.func,
  readOnly: PropTypes.bool,
  inputsOnly: PropTypes.bool,
};

export default CompanyCashStatement;
